'use client';

import {
  Banner,
  Breakpoint,
  Button,
  DialogBody,
  DialogFooter,
  Spinner,
  Stack,
  Text,
} from '@pie/amber';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { reporter } from '@upms/observability';
import { useEffect, useState } from 'react';
import { StripeSession, type PaymentMethodType } from '@/lib/api/billing';
import { ErrorBody } from '@/components/PaymentDialog/ErrorBody';
import { getStripe } from '@/lib/stripe/stripe';
import { usePaymentOutcome } from '@/components/PaymentDialog/PaymentOutcomeContext';
import { stripeAppearance } from './appearance';
import { PaymentForm } from './PaymentForm';

export type StripeSessionState = {
  /** Session (re)fetching on a method switch. */
  isLoading: boolean;
  /**
   * SetupIntent secret + publishable key for the selected method. The
   * `clientSecret` is per-method and used only at confirm time (never handed to
   * `<Elements>`, so a method switch never remounts it); kept populated across a
   * switch refetch via `keepPreviousData`.
   */
  data: StripeSession;
};

export type StripePaymentContentProps = {
  session: StripeSessionState;
  policyId: string;
  policyNumber: string;
  paymentMethodType: PaymentMethodType;
  onPaymentMethodTypeChange: (value: PaymentMethodType) => void;
  onSuccess: () => void;
  onClose: () => void;
  /** TODO(EE-643): temporary stopgap — true while waiting for Stripe→Billing replication; suppresses the success view. */
  isAwaitingReplication?: boolean;
  hasExistingPaymentMethod?: boolean;
};

const toStripePaymentMethodType = (type: PaymentMethodType) =>
  type === 'ach' ? 'us_bank_account' : 'card';

const StripeDialogReplicationBody = () => (
  <DialogBody>
    <Stack align="center" className="pb-layout-xl pt-layout-xl" direction="column" gap="2xs">
      <Spinner className="text-text-subtle-default" size="lg" title="Please wait" />
      <Text emphasis="subtle" variant="body-xs">
        Updating payment method
      </Text>
    </Stack>
  </DialogBody>
);

const StripeDialogSuccessBody = ({ onClose }: { onClose: () => void }) => (
  <>
    <DialogBody>
      <Banner header="Payment Method Updated" size="lg" tone="positive">
        Your payment method on file has been updated.
      </Banner>
    </DialogBody>
    <Breakpoint above="sm">
      <DialogFooter>
        <Stack direction="row" gap="sm" justify="end">
          <Button
            emphasis="heavy"
            fluid={{ base: true, sm: false }}
            onClick={onClose}
            size="md"
            tone="accent"
          >
            Done
          </Button>
        </Stack>
      </DialogFooter>
    </Breakpoint>
  </>
);

/**
 * Wraps Stripe `<Elements>` in deferred-SetupIntent mode (`mode: 'setup'`, no
 * `clientSecret`). Method switches call `elements.update()` in place —
 * remounting caused a scroll measure→restyle loop (PDESIGN-415). `clientSecret`
 * is forwarded to `PaymentForm` for `stripe.confirmSetup`.
 *
 * Also owns the inline success transition (EE-36): swaps the form for the
 * success body. `onSuccess` starts the replication wait in the parent; billing
 * cache invalidation happens later when the user closes the dialog.
 */
export const StripePaymentContent = ({
  session,
  policyId,
  policyNumber,
  paymentMethodType,
  onPaymentMethodTypeChange,
  onSuccess,
  onClose,
  isAwaitingReplication,
  hasExistingPaymentMethod = true,
}: StripePaymentContentProps) => {
  const [succeeded, setSucceeded] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const { report: reportOutcome } = usePaymentOutcome();

  const { publishableKey, clientSecret } = session.data;

  const handleSuccess = () => {
    setSucceeded(true);
    onSuccess();
  };

  useEffect(() => {
    reportOutcome({ provider: 'stripe' });
  }, [reportOutcome]);

  useEffect(() => {
    let active = true;
    getStripe(publishableKey)
      .then((instance) => {
        if (active) setStripe(instance);
      })
      .catch((error: unknown) => {
        reporter.error('Stripe.js failed to load', { feature: 'stripe', operation: 'load' }, error);
        if (active) {
          setLoadFailed(true);
          reportOutcome({ errorCategory: 'form_load' });
        }
      });
    return () => {
      active = false;
    };
  }, [publishableKey, reportOutcome]);

  if (succeeded) {
    return isAwaitingReplication ? (
      <StripeDialogReplicationBody />
    ) : (
      <StripeDialogSuccessBody onClose={onClose} />
    );
  }

  if (loadFailed) {
    return <ErrorBody />;
  }

  return (
    <Elements
      stripe={stripe}
      options={{
        mode: 'setup',
        currency: 'usd',
        paymentMethodTypes: [toStripePaymentMethodType(paymentMethodType)],
        appearance: stripeAppearance,
      }}
    >
      <PaymentForm
        isPaymentSessionLoading={session.isLoading}
        clientSecret={clientSecret}
        policyId={policyId}
        policyNumber={policyNumber}
        paymentMethodType={paymentMethodType}
        onPaymentMethodTypeChange={onPaymentMethodTypeChange}
        onSuccess={handleSuccess}
        hasExistingPaymentMethod={hasExistingPaymentMethod}
      />
    </Elements>
  );
};
